/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useLocale } from "@epic-mw/i18n";
import { ReactNode, useCallback, useEffect, useState } from "react";

import {
	CollapsibleCardViewport,
	CosmosButton,
	TitleDescStackProps,
	DocumentCard,
	CosmosLink,
	CosmosRichText,
	CosmosTextVariantEnum,
	EdsIcon,
	hasContent,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { flex } from "@panda/patterns";

import { LearnCard, LearnCardProps } from "./LearnCard.molecule";
import {
	CosmosElementLearnDocumentationCardFragment,
	CosmosEnumLearnPostType,
	CosmosEnumLearnTabs,
	ElementLearnCategoryFragment,
} from "@cosmos/hygraph-schema";

type LearnData = {
	title: string;
	description: string;
	thumbnail_image: {
		thumbnail_url: string;
	};
	banner_image: {
		banner_url: string;
	};
	profile: {
		name: string;
	};
	published_at: string;
	entity_type?: keyof typeof URL_PARAMS;
	entity_hash_id: string;
	entity_slug: string;
	slug: string;
	hash_id: string;
	translations: (LearnData & { locale: string })[];
	thread_comments_count: number;
	up_votes_count: number;
};

const URL_PARAMS = {
	tutorial: "tutorials",
	course: "courses",
	talks_and_demos: "talks-and-demos",
	recommended_community_tutorial: "recommended-community-tutorial",
	livestream: "livestreams",
	community_tutorial: "tutorials",
	learning_path: "paths",
	knowledge_base: "knowledge-base",
	module: "module",
	academic_resources: "academic-resources",
	sample_projects: "sample-projects",
};

const categoryWrapperStyles = css({
	minHeight: "eds.dimension.32",
	alignItems: "center",
	xxlDown: {
		alignItems: "flex-end",
	},
	smDown: {
		flexWrap: "wrap",
		alignItems: "flex-start",
	},
});
const titleWrapperStyles = css({
	maxWidth: "840px",
	lgDown: {
		flex: "0 1 100%",
	},
	smDown: {
		flex: "unset",
	},
});

export const isDocumentCard = (card: Card): card is CosmosElementLearnDocumentationCardFragment => {
	return card?.__typename === "CosmosElementLearnDocumentationCard";
};

type Card = LearnCardProps | CosmosElementLearnDocumentationCardFragment | null | undefined;

const renderCard = (card: Card, index: number, squareImage?: boolean): React.JSX.Element => {
	const key = `card-${index}`;
	if (isDocumentCard(card)) {
		const fullWidth = !index;
		return <DocumentCard key={key} fullWidth={fullWidth} {...card} />;
	} else {
		return <LearnCard key={key} {...card} industries={card?.industries || []} squareImage={squareImage} />;
	}
};

export type CategoryProps = ElementLearnCategoryFragment &
	Omit<TitleDescStackProps, "description"> & {
		activeTab?: string | null;
		ago?: string;
		applicationFamily?: string | null;
		remoteCards: {
			id: string;
			industries: CosmosEnumLearnTabs[];
			type: keyof typeof URL_PARAMS | CosmosEnumLearnPostType;
			data?: LearnData | null;
		}[];
	};

const getCards = (
	{ remoteCards, cards, activeTab }: CategoryProps,
	mapData: (a: LearnData) => LearnCardProps,
): Card[] =>
	remoteCards && remoteCards.length
		? (remoteCards
				.filter((data) => (activeTab ? data.industries?.includes(activeTab as CosmosEnumLearnTabs) : true))
				.map(({ data, type }) => {
					if (!data) return null;
					return mapData({ entity_type: type, ...data } as LearnData);
				})
				.filter((card): card is LearnCardProps => card !== null && card !== undefined) as Card[])
		: (cards || []).filter((data) =>
				!isDocumentCard(data) && activeTab
					? (data as LearnCardProps)?.industries?.includes(activeTab as CosmosEnumLearnTabs)
					: true,
			);

// Custom title component that can be conditionally wrapped in a link
const CategoryTitle = ({
	title,
	titleHref,
}: {
	title?: { text?: string; html?: string } | null;
	titleHref?: string | null;
}): React.JSX.Element | null => {
	if (!title) return null;

	const titleContent = title.html ? (
		<CosmosRichText
			variant={CosmosTextVariantEnum.headingMD}
			html={title.html}
			className={css({
				fontWeight: "bold",
			})}
		/>
	) : (
		<CosmosRichText
			variant={CosmosTextVariantEnum.headingMD}
			html={title.text || ""}
			className={css({
				fontWeight: "bold",
			})}
		/>
	);

	if (titleHref) {
		return (
			<CosmosLink
				href={titleHref}
				className={css({
					textDecoration: "none",
					display: "flex",
					alignItems: "center",
					gap: "eds.dimension.8",
					"&:hover": {
						"& svg": {
							transform: "translateX(2px)",
							_rtl: {
								transform: "translateX(-2px)",
							},
						},
					},
				})}
			>
				{titleContent}
				<EdsIcon
					icon="ChevronRight"
					size="sm"
					color="secondary"
					className={css({
						transition: "transform 0.2s ease-in-out",
					})}
				/>
			</CosmosLink>
		);
	}

	return titleContent;
};

export function sliceCount<T>(cards: T[], cardCount?: null | number): T[] {
	return cards.slice(0, cardCount ?? 4);
}

export function Category(props: CategoryProps): ReactNode {
	const {
		title,
		desc,
		remoteLearn,
		categoryType,
		activeTab,
		cardCount,
		cta,
		applicationFamily,
		remoteCards,
		cards,
		squareImage,
		titleHref,
	} = props;
	const locale = useLocale();
	const mappedGetLearnCardProps = useCallback(
		({
			title,
			description,
			thumbnail_image,
			profile,
			published_at,
			entity_type = "tutorial",
			entity_hash_id,
			entity_slug,
			hash_id,
			slug,
			translations,
			up_votes_count,
			thread_comments_count,
		}: LearnData): LearnCardProps => {
			let fTitle = title;
			let fDescription = description;
			let hashId = entity_hash_id || hash_id;
			let fSlug = entity_slug || slug;

			if (translations && translations.length > 0 && locale !== "en-US") {
				const translation = translations.find((o) => o.locale?.split("-")[0] === locale?.split("-")[0]);
				if (translation) {
					fTitle = translation.title;
					fDescription = translation.description;
					hashId = translation.entity_hash_id || translation.hash_id;
					fSlug = translation.entity_slug || translation.slug;
				}
			}

			return {
				title: {
					html: fTitle,
					text: fTitle,
				},
				desc: fDescription,
				img: {
					id: "",
					url: thumbnail_image?.thumbnail_url,
					optUrl: "",
				},
				source: {
					author: profile?.name,
					timestamp: published_at,
				},
				url: `https://dev.epicgames.com/community/learning/${URL_PARAMS[entity_type]}/${hashId}/${fSlug}`,
				upVotesCount: up_votes_count,
				threadCommentsCount: thread_comments_count,
				industries: [],
			};
		},
		[locale],
	);
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- GraphQL schema types remoteLearn.learnSearch.data as any
	const learnSearchData = remoteLearn?.learnSearch?.data;
	const defaultData =
		learnSearchData && Array.isArray(learnSearchData)
			? learnSearchData
					.map((item: unknown) => {
						if (item && typeof item === "object") {
							return mappedGetLearnCardProps(item as LearnData);
						}
						return null;
					})
					.filter((card: LearnCardProps | null): card is LearnCardProps => card !== null && card !== undefined)
			: getCards(props, mappedGetLearnCardProps);

	const [cardsToRender, setCardsToRenderPre] = useState<Card[]>(sliceCount(defaultData, cardCount));

	const setCardsToRender = useCallback(
		(cards: Card[]) => setCardsToRenderPre(sliceCount(cards, cardCount)),
		[cardCount],
	);

	useEffect(() => {
		if (remoteLearn?.id && categoryType && activeTab) {
			fetch(
				`/api/cosmos/learn?id=${remoteLearn.id}&categoryType=${categoryType}&industry=${activeTab}&applicationFamily=${applicationFamily}`,
			)
				.then((res) => res.json())
				.then((data: unknown) => {
					if (!Array.isArray(data)) {
						return;
					}
					const typedData = data as LearnData[];
					const someCards = sliceCount(typedData, cardCount);
					setCardsToRender(someCards.map(mappedGetLearnCardProps));
				})
				.catch((e) => {
					throw e;
				});
		} else {
			setCardsToRender(getCards({ remoteCards, cards, activeTab }, mappedGetLearnCardProps));
		}
	}, [
		remoteLearn,
		categoryType,
		activeTab,
		cardCount,
		applicationFamily,
		setCardsToRender,
		remoteCards,
		cards,
		locale,
		mappedGetLearnCardProps,
	]);

	const validDesc = hasContent(desc);

	return (
		<VStack
			className={cx("learn-category", "learn-category-count-" + cardCount)}
			gap="eds.dimension.16"
			alignItems="flex-start"
		>
			<HStack
				gap="eds.dimension.16"
				width="100%"
				justifyContent="space-between"
				className={cx(categoryWrapperStyles, validDesc ? css({ "&&": { alignItems: "flex-end" } }) : "")}
			>
				<VStack className={titleWrapperStyles} gap="eds.dimension.8" alignItems="flex-start">
					<CategoryTitle title={title} titleHref={titleHref} />
					{validDesc ? (
						<CosmosRichText variant={CosmosTextVariantEnum.paragraphSM} color="eds.text.secondary" html={desc} />
					) : null}
				</VStack>
				<CosmosButton {...cta} size="md" />
			</HStack>
			<CollapsibleCardViewport
				enabled={cardsToRender.length > 1}
				nVisibleChild={2}
				viewportCss={flex.raw({
					flexGrow: 1,
					flexDirection: { base: "row", smDown: "column" },
					gap: "eds.dimension.16",
					alignItems: "stretch",
					w: "full",
					flexBasis: "0",
					smToLg:
						cardsToRender.length > 3
							? {
									flexWrap: "wrap",
									"& > div": {
										flex: "40%",
										height: "auto",
									},
								}
							: undefined,
				})}
			>
				{
					// Assigned a default value to cards so it will never be null
					cardsToRender
						.filter(
							(card): card is LearnCardProps | CosmosElementLearnDocumentationCardFragment =>
								card !== null && card !== undefined,
						)
						.map((card, i) => renderCard(card, i, !!squareImage))
				}
			</CollapsibleCardViewport>
		</VStack>
	);
}
